import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_URL } from '../lib/utils';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';
import { Plus, Send, X } from 'lucide-react';

const US_STATES = ['Alabama','Alaska','Arizona','Arkansas','California','Colorado','Connecticut','Delaware','District of Columbia','Florida','Georgia','Hawaii','Idaho','Illinois','Indiana','Iowa','Kansas','Kentucky','Louisiana','Maine','Maryland','Massachusetts','Michigan','Minnesota','Mississippi','Missouri','Montana','Nebraska','Nevada','New Hampshire','New Jersey','New Mexico','New York','North Carolina','North Dakota','Ohio','Oklahoma','Oregon','Pennsylvania','Rhode Island','South Carolina','South Dakota','Tennessee','Texas','Utah','Vermont','Virginia','Washington','West Virginia','Wisconsin','Wyoming'];

const SubmitResourceModal = ({ categories = [], triggerClassName = 'bg-secondary hover:bg-secondary-hover text-white' }) => {
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const empty = {
    name: '', program_name: '', category_slug: '', description: '', address: '',
    city: '', county: '', state: '', zip_code: '', phone: '', website: '',
    contact_email: '', services: '', eligibility: '', cost: '',
    submitter_name: '', submitter_email: '', submitter_note: '',
    is_national: false,
    company_url: '', // honeypot — must stay empty
  };
  const [form, setForm] = useState(empty);

  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.category_slug) {
      toast.error('Organization name and category are required');
      return;
    }
    if (!form.is_national && (!form.city.trim() || !form.state)) {
      toast.error('Please add the city and state — or check "Nationwide Resource".');
      return;
    }
    if (!form.website.trim() && !form.phone.trim()) {
      toast.error('Please add a website OR a phone number (at least one).');
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API_URL}/resources/submit`, form);
      toast.success(data.message || 'Submitted for review — thank you!');
      setForm(empty);
      setOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not submit. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Button onClick={() => setOpen(true)} data-testid="submit-resource-btn"
        className={triggerClassName}>
        <Plus className="w-4 h-4 mr-2" /> Submit a Community Resource
      </Button>

      {open && (
        <div className="fixed inset-0 z-[100] flex items-start justify-center bg-black/50 p-4 overflow-y-auto" data-testid="submit-resource-modal">
          <div className="bg-white text-gray-900 rounded-2xl w-full max-w-2xl my-8 shadow-2xl">
            <div className="flex items-center justify-between p-5 border-b sticky top-0 bg-white rounded-t-2xl">
              <div>
                <h3 className="font-serif text-xl font-bold text-primary">Suggest a Community Resource — Free</h3>
                <p className="text-sm text-gray-500">Anyone can suggest a resource — you don't have to work for the organization. Every submission is reviewed by our team before it appears publicly. Not sure of a detail? Leave it blank or write "I don't know."</p>
              </div>
              <button onClick={() => setOpen(false)} data-testid="submit-resource-close" className="text-gray-400 hover:text-gray-700"><X className="w-5 h-5" /></button>
            </div>
            <form onSubmit={submit} className="p-5 space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label>Organization name *</Label>
                  <Input value={form.name} onChange={set('name')} required data-testid="sr-name" placeholder="e.g. Central Texas Food Bank" />
                </div>
                <div>
                  <Label>Program name</Label>
                  <Input value={form.program_name} onChange={set('program_name')} data-testid="sr-program" placeholder="e.g. Temple Pantry" />
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label>Category *</Label>
                  <select value={form.category_slug} onChange={set('category_slug')} required data-testid="sr-category"
                    className="mt-1 w-full h-10 rounded-md border border-gray-300 bg-white px-3 text-sm">
                    <option value="" disabled>Select a category…</option>
                    {categories.map((c) => <option key={c.slug} value={c.slug}>{c.label}</option>)}
                  </select>
                </div>
                <div>
                  <Label>Website <span className="text-gray-400 font-normal">(website or phone required)</span></Label>
                  <Input value={form.website} onChange={set('website')} data-testid="sr-website" placeholder="https://…" />
                </div>
              </div>
              <div>
                <Label>Description</Label>
                <Textarea value={form.description} onChange={set('description')} data-testid="sr-description" rows={2} placeholder="What does this organization provide?" />
              </div>
              <div className="rounded-lg bg-secondary/5 border border-secondary/20 p-3">
                <label className="flex items-center gap-2 cursor-pointer" data-testid="sr-nationwide-label">
                  <input type="checkbox" checked={form.is_national}
                    onChange={(e) => setForm({ ...form, is_national: e.target.checked })}
                    className="w-4 h-4 accent-[var(--secondary,#b8860b)]" data-testid="sr-nationwide" />
                  <span className="text-sm font-semibold text-gray-700">Nationwide Resource</span>
                  <span className="text-xs text-gray-500">(serves the whole U.S. — City, County &amp; State not required)</span>
                </label>
              </div>
              <div className={`grid sm:grid-cols-4 gap-4 ${form.is_national ? 'opacity-50 pointer-events-none' : ''}`}>
                <div><Label>City {form.is_national ? '' : '*'}</Label><Input value={form.city} onChange={set('city')} data-testid="sr-city" disabled={form.is_national} /></div>
                <div><Label>County</Label><Input value={form.county} onChange={set('county')} data-testid="sr-county" disabled={form.is_national} /></div>
                <div>
                  <Label>State {form.is_national ? '' : '*'}</Label>
                  <select value={form.state} onChange={set('state')} data-testid="sr-state" disabled={form.is_national} className="mt-1 w-full h-10 rounded-md border border-gray-300 bg-white px-3 text-sm">
                    <option value="">—</option>
                    {US_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div><Label>ZIP</Label><Input value={form.zip_code} onChange={set('zip_code')} data-testid="sr-zip" disabled={form.is_national} /></div>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div><Label>Phone <span className="text-gray-400 font-normal">(website or phone required)</span></Label><Input value={form.phone} onChange={set('phone')} data-testid="sr-phone" /></div>
                <div><Label>Contact email</Label><Input value={form.contact_email} onChange={set('contact_email')} data-testid="sr-contact-email" /></div>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div><Label>Services offered</Label><Input value={form.services} onChange={set('services')} data-testid="sr-services" /></div>
                <div><Label>Eligibility / cost</Label><Input value={form.eligibility} onChange={set('eligibility')} data-testid="sr-eligibility" /></div>
              </div>
              <div className="border-t pt-4">
                <p className="text-sm font-semibold text-gray-700 mb-2">How do you know about this resource? (helps us verify)</p>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div><Label>Your name</Label><Input value={form.submitter_name} onChange={set('submitter_name')} data-testid="sr-submitter-name" /></div>
                  <div><Label>Your email</Label><Input value={form.submitter_email} onChange={set('submitter_email')} data-testid="sr-submitter-email" /></div>
                </div>
                <div className="mt-3"><Label>Verification note / source</Label><Input value={form.submitter_note} onChange={set('submitter_note')} data-testid="sr-note" placeholder="e.g. I'm a social worker who refers clients here" /></div>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                {/* Honeypot: hidden from users, tempting to bots */}
                <div aria-hidden="true" style={{ position: 'absolute', left: '-9999px', top: 'auto', width: '1px', height: '1px', overflow: 'hidden' }}>
                  <label>Company URL<input type="text" tabIndex={-1} autoComplete="off" value={form.company_url} onChange={set('company_url')} data-testid="sr-company-url" /></label>
                </div>
                <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                <Button type="submit" disabled={submitting} className="bg-primary hover:bg-primary-hover text-white" data-testid="sr-submit">
                  <Send className="w-4 h-4 mr-2" /> {submitting ? 'Submitting…' : 'Submit for Review'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

export default SubmitResourceModal;
